import { useState } from "react";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { Send, Mail, MessageSquare } from "lucide-react";
import { updatePageTitle } from "../utils/titleUtils";
import Header from "../components/pages/home/Header";
import Footer from "../components/pages/home/Footer";
import GridBackground from "../components/ui/Linegrid";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    updatePageTitle("Contact Us");
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate required fields
    if (
      !formData.name ||
      !formData.email ||
      !formData.subject ||
      !formData.message
    ) {
      toast.error("Please fill in all fields");
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      // TODO: Replace with actual API endpoint when backend is ready
      // For now, just simulate a successful submission
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.success("Thank you for contacting us! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-50 custom-scrollbar">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-bg-60 pt-32 pb-20">
        <GridBackground className="absolute inset-0 opacity-20" />
        <div className="relative z-10 w-[90%] max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-full bg-fg-60 flex items-center justify-center">
              <span className="text-bg-60 text-xs font-bold">@</span>
            </div>
            <h2 className="text-fg-60 text-base font-medium">Contact Us</h2>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-fg-60">Get in touch with</span>{" "}
            <span className="text-fg-40">us</span>
          </h1>
          <p className="text-fg-60 text-base md:text-lg max-w-2xl mx-auto">
            Have a question or need help? We're here to assist you. Send us a
            message and we'll respond as soon as possible.
          </p>
        </div>
      </div>

      {/* Contact Form Section */}
      <section className="py-20 bg-bg-70">
        <div className="w-[90%] max-w-4xl mx-auto">
          <div className="bg-bg-60 rounded-3xl p-8 md:p-12 shadow-lg">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Name Field */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-3 bg-bg-70 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 transition-all duration-200"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 bg-bg-70 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 transition-all duration-200"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              {/* Subject Field */}
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-3 bg-bg-70 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 transition-all duration-200"
                  placeholder="What is this regarding?"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              {/* Message Field */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  className="w-full px-4 py-3 bg-bg-70 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 transition-all duration-200 resize-none"
                  placeholder="Tell us more about your inquiry..."
                  value={formData.message}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg text-base font-medium text-white dark:text-black transition-all duration-200 ${
                  loading
                    ? "bg-ac-02 bg-opacity-50 cursor-not-allowed"
                    : "bg-ac-02 hover:bg-ac-01 shadow-md hover:shadow-lg"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white dark:border-black border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-bg-60 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-ac-02 flex items-center justify-center flex-shrink-0">
                <Mail className="text-white dark:text-black" size={20} />
              </div>
              <div>
                <h3 className="text-fg-40 font-semibold mb-2">Email</h3>
                <a
                  href="mailto:info@leebai.com"
                  className="text-fg-60 hover:text-ac-02 transition-colors"
                >
                  info@leebai.com
                </a>
              </div>
            </div>

            <div className="bg-bg-60 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-ac-02 flex items-center justify-center flex-shrink-0">
                <MessageSquare
                  className="text-white dark:text-black"
                  size={20}
                />
              </div>
              <div>
                <h3 className="text-fg-40 font-semibold mb-2">Response Time</h3>
                <p className="text-fg-60">
                  We typically respond within 24-48 hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Contact;
